import { classNames } from "primereact/utils";
import styles from "./AuditTimeline.module.scss";
import { IUserAuditLogItem } from "./models/historyModels";

interface IAuditTimelineProps {
  items: IUserAuditLogItem[];
  timezone?: string;
  onDetailClick: (detailId: number) => void;
  generateText: (
    action: number,
    payload: Record<string, any>,
    actor: string | null | undefined,
    timezone?: string,
  ) => string;
  getCategory: (action: number) => string;
  categoryColors: Record<string, string>;
}

function formatTimestamp(iso: string, timezone?: string): { date: string; time: string } {
  // Ensure the string is parsed as UTC — append Z if no timezone offset is present
  const utcIso =
    iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
  const d = new Date(utcIso);
  const tzOpts: Intl.DateTimeFormatOptions = timezone ? { timeZone: timezone } : {};
  const date = d.toLocaleDateString("bg-BG", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...tzOpts,
  });
  const time = d.toLocaleTimeString("bg-BG", {
    hour: "2-digit",
    minute: "2-digit",
    ...tzOpts,
  });
  return { date, time };
}

export const AuditTimeline = ({
  items,
  timezone,
  onDetailClick,
  generateText,
  getCategory,
  categoryColors,
}: IAuditTimelineProps) => {
  return (
    <div className={styles.timeline}>
      {items.map((item, index) => {
        const category = getCategory(item.action);
        const color = categoryColors[category] ?? "#6366f1";

        // Anchor alternation to the oldest item (bottom) so that adding a new
        // item at the top never shifts the position of any existing item.
        const isLeft = (items.length - 1 - index) % 2 === 0;

        let payload: Record<string, any> = {};
        try {
          if (item.payload) payload = JSON.parse(item.payload);
        } catch {}

        const text = generateText(item.action, payload, item.actorDisplayName, timezone);
        const { date, time } = formatTimestamp(item.timestamp, timezone);

        return (
          <div
            key={item.id}
            className={classNames(styles.entry, isLeft ? styles.left : styles.right)}
          >
            {/* Left column — card (even from bottom) or date (odd from bottom) */}
            {isLeft ? (
              <div className={styles.card_side}>
                <EntryCard
                  text={text}
                  time={time}
                  date={date}
                  detailId={item.detailId}
                  onDetailClick={onDetailClick}
                />
              </div>
            ) : (
              <div className={styles.date_side}>
                <span className={styles.date_badge}>{date} · {time}</span>
              </div>
            )}

            {/* Center dot with connecting line via ::before/::after */}
            <div className={styles.dot_col}>
              <div
                className={styles.dot}
                style={{
                  backgroundColor: color,
                  boxShadow: `0 0 8px ${color}80`,
                }}
              />
            </div>

            {/* Right column — date (even from bottom) or card (odd from bottom) */}
            {isLeft ? (
              <div className={styles.date_side}>
                <span className={styles.date_badge}>{date} · {time}</span>
              </div>
            ) : (
              <div className={styles.card_side}>
                <EntryCard
                  text={text}
                  time={time}
                  date={date}
                  detailId={item.detailId}
                  onDetailClick={onDetailClick}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface IEntryCardProps {
  text: string;
  time: string;
  date: string;
  detailId: number | null;
  onDetailClick: (id: number) => void;
}

const EntryCard = ({ text, time, date, detailId, onDetailClick }: IEntryCardProps) => (
  <div className={styles.card}>
    <p className={styles.card_text} dangerouslySetInnerHTML={{ __html: text }} />
    <div className={styles.card_footer}>
      <span className={styles.card_time}>
        <span className={styles.mobile_date}>{date} · </span>
        {time}
      </span>
      {detailId !== null && (
        <button
          className={styles.detail_btn}
          onClick={() => onDetailClick(detailId)}
        >
          Детайли
        </button>
      )}
    </div>
  </div>
);
